<template>
  <div class="sf-modal-white sf-modal sf-modal-top sf-modal-l">
    <div class="sf-modal-backdrop">
      <div class="sf-modal-container">
        <div class="sf-modal-inner">
          <div class="sf-modal-header">
            <div class="sf-modal-back sf-invisible"></div>
            <div class="sf-modal-title">
              {{ I18n.t('user.new_user_with_role.' + role) }}
            </div>
            <div class="sf-modal-close" @click="$emit('close-add-modal')"></div>
          </div>
          <div class="sf-pad-3">
            <div
              v-if="hasUserErrors"
              class="
                sf-alert
                sf-alert-danger
                sf-alert-large
                sf-below-2
                sf-level-vertical
              "
            >
              <div
                v-for="userError in userErrors"
                :key="userError.message"
                class="sf-level-item"
              >
                <div class="sf-fwdb sf-c-danger-dark sf-font-m">
                  {{ userError.user }}
                </div>
                <div>{{ userError.message }}</div>
              </div>
            </div>
            <table class="sf-table sf-table-border">
              <tbody>
                <tr>
                  <th>{{ I18n.t('common.name') }}</th>
                  <th>{{ I18n.t('common.email') }}</th>
                  <th>{{ I18n.t('common.language') }}</th>
                  <th
                    v-if="company.firmSupportsAdminUsers"
                    v-tooltip="I18n.t('user.admin_user_permissions_label')"
                  >
                    <div class="sf-level sf-level-small">
                      <span class="sf-level-item">{{
                        I18n.t('user.admin_user_permissions_table_header')
                      }}</span
                      ><span class="sf-level-item fa fa-info-circle"></span>
                    </div>
                  </th>
                  <th v-if="ssoEntities.length > 0">
                    {{
                      I18n.t(
                        'activerecord.attributes.global_user.sso_entity_id'
                      )
                    }}
                  </th>
                </tr>
                <tr
                  v-for="(user, index) in newUsers"
                  :key="index"
                  :ref="'user' + index"
                  :user="user"
                  class="sf-table-middle"
                >
                  <td>
                    <sf-typeahead
                      v-if="user.userId == ''"
                      :data="selectableUsers"
                      :filter-property="getName"
                      :index="index"
                      :on-select="onUserSelect"
                      :set-property="setName"
                    ></sf-typeahead
                    ><input
                      v-if="user.userId"
                      v-model="user.name"
                      class="sf-form-input sf-full"
                      type="text"
                    />
                  </td>
                  <td>
                    <sf-typeahead
                      v-if="user.userId == ''"
                      :data="selectableUsers"
                      :filter-property="getEmail"
                      :index="index"
                      :on-select="onUserSelect"
                      :set-property="setEmail"
                    ></sf-typeahead
                    ><input
                      v-if="user.userId"
                      v-model="user.email"
                      class="sf-form-input sf-full"
                      type="text"
                    />
                  </td>
                  <td>
                    <div class="sf-form-select-wrapper sf-full">
                      <select v-model="user.locale" class="sf-form-select">
                        <option
                          v-for="locale in company.availableLocales"
                          :key="locale"
                          :value="locale"
                        >
                          {{ I18n.t('js.languages.' + locale) }}
                        </option>
                      </select>
                    </div>
                  </td>
                  <td v-if="company.firmSupportsAdminUsers" class="sf-align-c">
                    <sf-checkbox v-model="user.usersAdmin"></sf-checkbox>
                  </td>
                  <td v-if="ssoEntities.length > 0" class="sf-align-c">
                    <div class="sf-form-select-wrapper sf-full">
                      <select
                        v-model="user.ssoEntityId"
                        :disabled="user.userId != ''"
                        class="sf-form-select"
                      >
                        <option value=""></option>
                        <option
                          v-for="ssoEntity in ssoEntities"
                          :key="ssoEntity.id"
                          :value="ssoEntity.id"
                        >
                          {{ ssoEntity.subdomain }} ({{ ssoEntity.ssoType }})
                        </option>
                      </select>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="sf-align-c sf-pad-b-3">
            <a class="sf-btn sf-btn-success" @click.prevent="submitUsers()">{{
              I18n.t('common.save')
            }}</a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '../api/index'
import SfTypeahead from './sf-typeahead'
import SfCheckbox from '@app/shared/vue/components/sf-checkbox'
import escape from 'lodash/escape'
import SfBannerPage from '@app/shared/vue/components/sf-banner/sf-banner-page.vue'

const AddUserModal = {
  components: {
    SfTypeahead,
    SfCheckbox,
    SfBannerPage,
  },

  props: {
    role: {
      type: String,
      required: true,
    },
    typeaheadUsers: {
      type: Array,
      required: true,
    },
    company: {
      type: Object,
      required: true,
    },
    ssoEntities: {
      type: Array,
      required: true,
    },
  },

  data() {
    return {
      newUsers: [
        {
          userId: '',
          email: '',
          name: '',
          locale: this.company.firmLocale,
          roleId: '',
          role: this.role,
          usersAdmin: false,
          ssoEntityId: '',
        },
        {
          userId: '',
          email: '',
          name: '',
          locale: this.company.firmLocale,
          roleId: '',
          role: this.role,
          usersAdmin: false,
          ssoEntityId: '',
        },
        {
          userId: '',
          email: '',
          name: '',
          locale: this.company.firmLocale,
          roleId: '',
          role: this.role,
          usersAdmin: false,
          ssoEntityId: '',
        },
        {
          userId: '',
          email: '',
          name: '',
          locale: this.company.firmLocale,
          roleId: '',
          role: this.role,
          usersAdmin: false,
          ssoEntityId: '',
        },
      ],
      userErrors: [],
    }
  },

  emits: ['close-add-modal', 'refresh-users'],

  computed: {
    alreadyAddedUserIds: function alreadyAddedUserIds() {
      return this.newUsers.reduce((acc, user) => {
        if (!user.userId) {
          return acc
        }

        acc[user.userId] = true
        return acc
      }, {})
    },

    selectableUsers: function selectableUsers() {
      return this.typeaheadUsers.filter(
        (user) =>
          !this.alreadyAddedUserIds[user.id] &&
          ((this.role === 'firm' && user.internal) ||
            (this.role === 'external' && !user.internal) ||
            this.role === 'customer')
      )
    },

    hasUserErrors() {
      return this.userErrors.length > 0
    },
  },

  methods: {
    addErrorsToHeader(errors) {
      this.userErrors = errors
    },

    submitUsers() {
      api
        .createUsers(this.newUsers)
        .then(() => this.$emit('refresh-users'))
        .catch((error) => this.addErrorsToHeader(error.body.errors))
    },

    onUserSelect(item, index) {
      this.newUsers[index] = {
        userId: item['id'],
        email: item['email'],
        name: item['name'],
        locale: item['locale'],
        roleId: item['roleId'],
        role: this.role,
      }
    },

    setName(index, name) {
      this.newUsers[index].name = name
    },

    setEmail(index, email) {
      this.newUsers[index].email = email
    },

    getName(item) {
      return escape(item.name)
    },

    getEmail(item) {
      return item.email
    },
  },
}

export default AddUserModal
</script>
